import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Landmark, Plus, Pencil, Trash2, DollarSign, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatCard } from '@/components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormDialog } from '@/components/ui/form-dialog';
import api from '@/lib/api';
import type { BankAccount } from '@/types/bank';
import { formatCurrency, cn } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

const ACCOUNT_TYPE_STYLES: Record<string, string> = {
  CURRENT: 'bg-primary/10 text-primary-text ring-primary',
  SAVINGS: 'bg-success/10 text-success-text ring-success',
  CASH: 'bg-warning/10 text-warning-text ring-warning',
};

/** List row as rendered/edited on this page (name/number are always present on saved accounts). */
interface BankAccountRow extends BankAccount {
  bankName: string;
  accountNumber: string;
}

interface BankAccountPayload {
  name: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  openingBalance: number;
  currency: string;
  notes: string;
}

const emptyForm = {
  name: '',
  bankName: '',
  accountNumber: '',
  accountType: 'CURRENT',
  openingBalance: '',
  currency: 'PKR',
  notes: '',
};

export function BankAccountsPage() {
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('finance');
  const canWrite = hasPermission('bank:write') || hasPermission('bank:*');
  const canDelete = hasPermission('bank:delete') || hasPermission('bank:*');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 25;
  const queryClient = useQueryClient();

  const { data: bankData, isLoading } = useQuery({
    queryKey: ['bank-accounts', page],
    queryFn: async () => {
      const res = await api.get('/bank-accounts', { params: { page, limit } });
      return res.data;
    },
  });

  const accounts: BankAccountRow[] = bankData?.data ?? (Array.isArray(bankData) ? bankData : []);
  const bankTotal: number = bankData?.meta?.total ?? bankData?.total ?? accounts.length;

  const saveMutation = useMutation({
    mutationFn: (data: BankAccountPayload) =>
      editingId
        ? api.patch(`/bank-accounts/${editingId}`, data)
        : api.post('/bank-accounts', data),
    onSuccess: () => {
      toast.success(editingId ? t('bankAccountUpdated') : t('bankAccountCreated'));
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      closeDialog();
    },
    onError: (err) => notify.error(err, t('failedToSaveBankAccount')),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/bank-accounts/${id}`),
    onSuccess: () => {
      toast.success(t('bankAccountDeactivated'));
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
    onError: (err) => notify.error(err, t('failedToDeactivateAccount')),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (account: BankAccountRow) => {
    setEditingId(account.id);
    setForm({
      name: account.name,
      bankName: account.bankName,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
      openingBalance: String(account.openingBalance),
      currency: account.currency,
      notes: account.notes ?? '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = () => {
    if (!form.name || !form.bankName || !form.accountNumber) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    saveMutation.mutate({
      ...form,
      openingBalance: Number(form.openingBalance) || 0,
    });
  };

  const totalBalance = accounts.reduce((s: number, a: BankAccountRow) => s + Number(a.currentBalance ?? 0), 0);

  return (
    <div className="space-y-6 page-enter">
      {/* Header */}
      <PageHeader
        title={t('bankAccounts')}
        subtitle={t('manageBankAccounts')}
        actions={canWrite && (
          <Button onClick={openCreate}>
            <Plus className="me-2 h-4 w-4" />
            {t('newAccount')}
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <StatCard className="animate-fade-in-up stagger-1" label={t('totalAccounts')} value={accounts.length} icon={Landmark} />
        <StatCard
          className="animate-fade-in-up stagger-2"
          label={t('totalBalance')}
          value={totalBalance}
          format="currency"
          icon={DollarSign}
          accent="success"
        />
        <StatCard
          className="animate-fade-in-up stagger-3"
          label={t('activeAccounts')}
          value={accounts.filter((a: BankAccountRow) => a.isActive).length}
          icon={Building2}
          accent="info"
        />
      </div>

      {/* Table */}
      <Card className="animate-fade-in-up stagger-4">
        <CardHeader>
          <CardTitle className="text-lg">{t('bankAccountsList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={4} cols={6} />
          ) : !accounts.length ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Landmark className="mb-3 h-12 w-12 text-muted-foreground/40 animate-pulse-soft" />
              <p className="text-sm font-medium text-muted-foreground">{t('noBankAccountsYet')}</p>
              <p className="mt-1 text-xs text-muted-foreground/70">{t('addFirstBankAccount')}</p>
            </div>
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('name')}</Th>
                    <Th>{t('bankName')}</Th>
                    <Th>{t('accountNumber')}</Th>
                    <Th>{t('type')}</Th>
                    <Th className="text-end">{t('balance')}</Th>
                    <Th className="text-end">{t('actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {accounts.map((account: BankAccountRow) => (
                    <Tr key={account.id}>
                      <Td className="font-medium td-name">{account.name}</Td>
                      <Td label={t('colBank')} className="text-muted-foreground">{account.bankName}</Td>
                      <Td label={t('accountNumber')} className="font-mono text-xs text-muted-foreground td-hide">{account.accountNumber}</Td>
                      <Td label={t('type')}>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                            ACCOUNT_TYPE_STYLES[account.accountType] ?? '',
                          )}
                        >
                          {account.accountType}
                        </span>
                      </Td>
                      <Td label={t('balance')} className="text-end font-semibold text-success-text">
                        {formatCurrency(account.currentBalance ?? account.openingBalance)}
                      </Td>
                      <Td className="text-end td-actions">
                        <TableRowActions
                          actions={[
                            {
                              key: 'edit',
                              label: 'Edit',
                              icon: Pencil,
                              onSelect: () => openEdit(account),
                              permission: canWrite,
                              primary: true,
                            },
                            {
                              key: 'delete',
                              label: 'Delete',
                              icon: Trash2,
                              onSelect: () => setConfirmDeleteId(account.id),
                              permission: canDelete,
                              variant: 'destructive',
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
          )}
          {/* Pagination */}
          {bankTotal > limit && (
            <div className="border-t pt-4 mt-2">
              <PaginationBar page={page} pageSize={limit} total={bankTotal} onPageChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editingId ? t('editBankAccount') : t('addBankAccount')}
        width="48rem"
        submitLabel={editingId ? t('updateAccount') : t('createAccount')}
        isPending={saveMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t('accountName')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              placeholder={t('accountNamePlaceholder')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">
              {t('bankName')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              placeholder={t('bankNamePlaceholder')}
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">
              {t('accountNumber')} <span className="text-destructive-text">*</span>
            </Label>
            <Input
              dir="ltr"
              placeholder={t('accountNumberPlaceholder')}
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('accountType')}</Label>
            <Combobox
              options={[
                { value: 'CURRENT', label: t('accountTypeCurrent') },
                { value: 'SAVINGS', label: t('accountTypeSavings') },
                { value: 'CASH', label: t('accountTypeCash') },
              ]}
              value={form.accountType}
              onChange={(val) => setForm({ ...form, accountType: val || 'CURRENT' })}
              placeholder={t('selectType')}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('openingBalance')}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.openingBalance}
                onChange={(e) => setForm({ ...form, openingBalance: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('currency')}</Label>
              <Input
                placeholder="PKR"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Textarea
              placeholder={t('notesPlaceholder')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      </FormDialog>
      <ConfirmDialog
        open={!!confirmDeleteId}
        title={t('deactivateBankAccount')}
        message={t('deactivateAccountMessage')}
        confirmLabel={t('deactivate')}
        loading={deactivateMutation.isPending}
        onConfirm={() => {
          if (confirmDeleteId) {
            deactivateMutation.mutate(confirmDeleteId, {
              onSettled: () => setConfirmDeleteId(null),
            });
          }
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
